import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { axios, useAuth } from '../../context/AuthContext';

// ─── Sabitler ────────────────────────────────────────────────────────────────

const DURUM_LABEL = {
  kurum_ogrencisi: 'Kurum',
  misafir_ogrenci: 'Misafir',
};

const DERS_RENK = {
  'Türkçe':              '#6366f1',
  'T.C. İnkılap Tarihi': '#f97316',
  'Din Kültürü':          '#a855f7',
  'İngilizce':            '#ec4899',
  'Sosyal Bilgiler':     '#f59e0b',
  'Matematik':           '#10b981',
  'Fen Bilimleri':       '#3b82f6',
  'Matematik (AYT)':     '#10b981',
};

const DERS_ORDER = [
  'Türkçe', 'T.C. İnkılap Tarihi', 'Din Kültürü', 'İngilizce',
  'Sosyal Bilgiler', 'Matematik', 'Matematik (AYT)', 'Fen Bilimleri',
];

const TUR_STYLE = {
  TYT: { bg: '#ede9fe', color: '#5b21b6' },
  AYT: { bg: '#fef3c7', color: '#92400e' },
  LGS: { bg: '#dbeafe', color: '#1d4ed8' },
};

const getTurStyle = (tur) => TUR_STYLE[tur] ?? { bg: '#e2e8f0', color: '#334155' };
const getPuanLabel = (tur) => (tur === 'AYT' ? 'SAY Puan' : tur === 'LGS' ? 'LGS Puan' : 'TYT Puan');

// ─── Yardımcılar ─────────────────────────────────────────────────────────────

const fmt = (v, dec = 2) => {
  if (v == null || v === '') return '-';
  const n = parseFloat(v);
  return isNaN(n) ? '-' : n.toFixed(dec);
};

const pick = (obj, keys, fb = '-') => {
  for (const k of keys) {
    if (obj?.[k] != null && obj[k] !== '') return obj[k];
  }
  return fb;
};

const formatDate = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (isNaN(d)) return '-';
  return d.toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const getList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.sonuclar)) return payload.data.sonuclar;
  if (Array.isArray(payload?.sonuclar)) return payload.sonuclar;
  return [];
};

const getDetailResults = (payload) => {
  if (Array.isArray(payload?.data?.sonuclar)) return payload.data.sonuclar;
  if (Array.isArray(payload?.sonuclar)) return payload.sonuclar;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

// ─── Scroll Lock Hook ─────────────────────────────────────────────────────────

let modalScrollLockCount = 0;
function useModalScrollLock(isOpen = true) {
  useEffect(() => {
    if (!isOpen) return;
    modalScrollLockCount++;
    if (modalScrollLockCount === 1) {
      document.body.dataset.prevOverflow = document.body.style.overflow || '';
      document.body.style.overflow = 'hidden';
    }
    return () => {
      modalScrollLockCount = Math.max(0, modalScrollLockCount - 1);
      if (modalScrollLockCount === 0) {
        document.body.style.overflow = document.body.dataset.prevOverflow || '';
        delete document.body.dataset.prevOverflow;
      }
    };
  }, [isOpen]);
}

// ─── useWindowWidth ───────────────────────────────────────────────────────────

function useWindowWidth() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return w;
}

// ─── Animasyonlu Yükleme ──────────────────────────────────────────────────────

function UploadAnimation({ fileName }) {
  const steps = [
    { icon: '📄', label: 'PDF okunuyor…' },
    { icon: '🔍', label: 'Öğrenci satırları ayrıştırılıyor…' },
    { icon: '🔗', label: 'Kurum öğrencileri eşleştiriliyor…' },
    { icon: '💾', label: 'Veritabanına kaydediliyor…' },
  ];
  const [step, setStep] = useState(0);
  const [dots, setDots] = useState('');
  useEffect(() => {
    const t1 = setInterval(() => setStep((s) => Math.min(s + 1, steps.length - 1)), 1800);
    const t2 = setInterval(() => setDots((d) => (d.length >= 3 ? '' : d + '.')), 400);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, []);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 16px' }}>
      <div style={{
        background: 'var(--card-bg, #fff)', border: '1px solid var(--border-color, #e2e8f0)',
        borderRadius: 20, padding: '32px 28px', width: '100%', maxWidth: 400,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        boxShadow: '0 8px 40px rgba(0,0,0,0.10)', boxSizing: 'border-box',
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          border: '4px solid var(--border-color, #e2e8f0)',
          borderTop: '4px solid var(--accent,#6366f1)',
          animation: 'spin 0.9s linear infinite',
        }} />
        <div style={{ marginTop: 20, fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)', textAlign: 'center', wordBreak: 'break-all', width: '100%' }}>
          {fileName}
        </div>
        <div style={{ marginTop: 20, width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {steps.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: i <= step ? 1 : 0.22, transition: 'opacity 0.5s' }}>
              <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{s.icon}</span>
              <span style={{ fontSize: '0.85rem', color: i === step ? 'var(--accent,#6366f1)' : 'var(--text-muted)', fontWeight: i === step ? 700 : 400, transition: 'color 0.4s' }}>
                {s.label}{i === step ? dots : i < step ? ' ✓' : ''}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Ders Net Bar ─────────────────────────────────────────────────────────────

function DersBar({ label, dogru, yanlis, net, soru, renk }) {
  const pct = soru > 0 ? Math.min(100, (dogru / soru) * 100) : 0;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: renk }}>{label}</span>
        <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)', fontWeight: 500 }}>
          <b style={{ color: 'var(--text-primary)', fontSize: '0.85rem' }}>{fmt(net)}</b> net &nbsp;·&nbsp;
          <span style={{ color: '#10b981' }}>{dogru}D</span> <span style={{ color: '#ef4444' }}>{yanlis}Y</span> {soru ? soru - dogru - yanlis : '-'}B
        </span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: 'var(--border-color,#e5e7eb)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg,${renk}cc,${renk})`, borderRadius: 4, transition: 'width 0.9s ease' }} />
      </div>
    </div>
  );
}

// ─── Karne Modal (Öğrenci Detay Modalı - Üstteki Modal) ──────────────────────
// Mobilde: bottom sheet
// Masaüstünde: centered dialog
// z-index: 10100 (Büyük sonuçlar modalının üzerinde açılır)

function KarneModal({ row, onClose }) {
  const w = useWindowWidth();
  const isMobile = w < 640;
  const dersler = row?.dersler ?? {};
  const altDersler = Object.fromEntries(
    Object.entries(row?.alt_dersler ?? {}).filter(([key]) => key !== '__rapor'),
  );
  const visibleDersler = DERS_ORDER.filter((d) => dersler[d]);

  useModalScrollLock(true);

  useEffect(() => {
    const fn = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.stopImmediatePropagation?.();
        onClose();
      }
    };
    window.addEventListener('keydown', fn, true);
    return () => window.removeEventListener('keydown', fn, true);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      data-modal="karne"
      data-nested-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 10100,
        background: 'rgba(0,0,0,0.60)',
        backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : '16px',
        boxSizing: 'border-box',
        animation: 'fadeIn 0.18s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card-bg,#fff)',
          width: '100%',
          maxWidth: isMobile ? '100%' : 580,
          maxHeight: isMobile ? '93dvh' : 'calc(100dvh - 32px)',
          borderRadius: isMobile ? '22px 22px 0 0' : 18,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          boxSizing: 'border-box',
          boxShadow: isMobile
            ? '0 -8px 40px rgba(0,0,0,0.25)'
            : '0 24px 60px rgba(0,0,0,0.28), 0 0 0 1px rgba(0,0,0,0.08)',
          animation: isMobile ? 'slideUpSheet 0.32s cubic-bezier(.22,1,.36,1)' : 'slideUp 0.25s ease',
        }}
      >
        {/* Handle bar – sadece mobilde */}
        {isMobile && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12, paddingBottom: 4 }}>
            <div style={{ width: 44, height: 5, borderRadius: 3, background: 'var(--border-color,#d1d5db)' }} />
          </div>
        )}

        <div style={{ padding: isMobile ? '10px 18px 36px' : '26px 26px 22px' }}>

          {/* ─── Öğrenci başlığı ─── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 18 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 900, fontSize: isMobile ? '1.12rem' : '1.28rem', color: 'var(--text-primary)', wordBreak: 'break-word', lineHeight: 1.2 }}>
                {row.ad_soyad}
              </div>
              <div style={{ marginTop: 7, display: 'flex', flexWrap: 'wrap', gap: '5px 8px', alignItems: 'center' }}>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{row.sinif_adi ?? '-'}</span>
                {row.ogrenci_no && <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>#{row.ogrenci_no}</span>}
                <Pill
                  label={DURUM_LABEL[row.durum] ?? row.durum}
                  bg={row.durum === 'kurum_ogrencisi' ? '#d1fae5' : '#e0e7ff'}
                  color={row.durum === 'kurum_ogrencisi' ? '#065f46' : '#3730a3'}
                />
                {row.tur && (
                  <Pill
                    label={row.tur}
                    bg={getTurStyle(row.tur).bg}
                    color={getTurStyle(row.tur).color}
                  />
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label="Kapat"
              style={{
                background: 'var(--bg-subtle,#f1f5f9)', border: 'none',
                width: 34, height: 34, borderRadius: '50%', cursor: 'pointer',
                fontSize: '1rem', color: 'var(--text-muted)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}
            >
              ✕
            </button>
          </div>

          {/* ─── Stat kutucukları ─── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 22 }}>
            {[
              { label: 'Okul Sırası', value: row.okul_sirasi ?? '-', emoji: '🏫' },
              { label: 'Genel Sıra',  value: row.genel_siralama ? `#${Number(row.genel_siralama).toLocaleString('tr-TR')}` : '-', emoji: '🌍' },
              { label: 'Top. Net',    value: fmt(row.toplam_net), emoji: '📊' },
              { label: getPuanLabel(row.tur), value: fmt(row.puan), emoji: '🎯' },
            ].map((s) => (
              <div key={s.label} style={{
                background: 'var(--bg-subtle,#f8fafc)',
                border: '1.5px solid var(--border-color,#e5e7eb)',
                borderRadius: 12, padding: '10px 6px', textAlign: 'center',
              }}>
                <div style={{ fontSize: isMobile ? '1rem' : '1.1rem', marginBottom: 3 }}>{s.emoji}</div>
                <div style={{
                  fontSize: 'clamp(0.88rem,3vw,1.1rem)', fontWeight: 900,
                  color: 'var(--text-primary)', lineHeight: 1,
                }}>{s.value}</div>
                <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', fontWeight: 600, marginTop: 4, lineHeight: 1.3 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* ─── Ders netleri ─── */}
          {visibleDersler.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <SectionTitle>📚 Ders Netleri</SectionTitle>
              <div style={{
                background: 'var(--bg-subtle,#f8fafc)',
                border: '1px solid var(--border-color,#e5e7eb)',
                borderRadius: 12, padding: '14px 16px',
              }}>
                {visibleDersler.map((d) => {
                  const info = dersler[d];
                  return (
                    <DersBar key={d} label={d} dogru={info.dogru ?? 0} yanlis={info.yanlis ?? 0}
                      net={info.net} soru={info.soru ?? 40} renk={DERS_RENK[d] ?? '#64748b'} />
                  );
                })}
              </div>
            </div>
          )}

          {/* ─── Alt ders detayları ─── */}
          {Object.keys(altDersler).length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <SectionTitle>🔍 Alt Ders Detayları</SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(138px,1fr))', gap: 8 }}>
                {Object.entries(altDersler).map(([ad, info]) => (
                  <div key={ad} style={{
                    background: 'var(--bg-subtle,#f8fafc)',
                    border: '1px solid var(--border-color,#e5e7eb)',
                    borderRadius: 10, padding: '9px 12px',
                  }}>
                    <div style={{ fontWeight: 700, fontSize: '0.76rem', color: 'var(--text-primary)', marginBottom: 5 }}>{ad}</div>
                    <div style={{ display: 'flex', gap: 8, fontSize: '0.74rem' }}>
                      <span style={{ color: '#10b981', fontWeight: 600 }}>D:{info.d ?? '-'}</span>
                      <span style={{ color: '#ef4444', fontWeight: 600 }}>Y:{info.y ?? '-'}</span>
                      <span style={{ color: 'var(--accent,#6366f1)', fontWeight: 700 }}>N:{fmt(info.net)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── Kapat butonu ─── */}
          <button onClick={onClose} style={{
            width: '100%', padding: '13px 0', borderRadius: 12, marginTop: 6,
            background: 'var(--accent,#6366f1)', color: '#fff', border: 'none',
            fontWeight: 700, fontSize: '0.92rem', cursor: 'pointer', letterSpacing: '0.02em',
            transition: 'opacity 0.15s',
          }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.88'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Deneme Düzenle Modal ───────────────────────────────────────────────────
// z-index: 10200 (Her şeyin üzerinde açılır)

function DenemeDuzenleModal({ kayit, onClose, onSaved, onDeleted }) {
  const [baslik, setBaslik] = useState(pick(kayit, ['baslik', 'title'], ''));
  const [tur, setTur] = useState(kayit?.tur || 'TYT');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [yeniPdf, setYeniPdf] = useState(null);
  const [replacingPdf, setReplacingPdf] = useState(false);
  const editFileRef = useRef();
  const w = useWindowWidth();
  const isMobile = w < 640;

  useModalScrollLock(true);

  useEffect(() => {
    const fn = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.stopImmediatePropagation?.();
        onClose();
      }
    };
    window.addEventListener('keydown', fn, true);
    return () => window.removeEventListener('keydown', fn, true);
  }, [onClose]);

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!baslik.trim()) {
      toast.error('Lütfen bir deneme başlığı girin.');
      return;
    }
    setSaving(true);
    try {
      const id = kayit.id ?? kayit._id;
      const res = await axios.put(`/turkiye-geneli-deneme-sonuclari/${id}`, {
        baslik: baslik.trim(),
        tur,
      });
      toast.success(res.data?.message || 'Deneme başarıyla güncellendi.');
      onSaved(res.data?.data || { ...kayit, baslik: baslik.trim(), tur });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Deneme güncellenemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleReplacePdf = async () => {
    if (!yeniPdf) {
      toast.error('Lütfen yeni bir PDF dosyası seçin.');
      return;
    }
    const id = kayit.id ?? kayit._id;
    const formData = new FormData();
    formData.append('pdfFile', yeniPdf);
    if (baslik.trim()) formData.append('baslik', baslik.trim());
    setReplacingPdf(true);
    try {
      const res = await axios.post(`/turkiye-geneli-deneme-sonuclari/${id}/pdf-degistir`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data?.message || 'Deneme PDF\'i ve sonuçları başarıyla güncellendi.');
      onSaved(res.data?.data || { ...kayit, baslik: baslik.trim(), tur });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'PDF değiştirilemedi.');
    } finally {
      setReplacingPdf(false);
    }
  };

  const handleDelete = async () => {
    const baslikStr = pick(kayit, ['baslik', 'title'], 'Bu denemeyi');
    if (!window.confirm(`"${baslikStr}" kaydını ve bağlı tüm öğrenci sonuçlarını silmek istediğinizden emin misiniz?\n\nBu işlem geri alınamaz!`)) {
      return;
    }
    setDeleting(true);
    try {
      const id = kayit.id ?? kayit._id;
      const res = await axios.delete(`/turkiye-geneli-deneme-sonuclari/${id}`);
      toast.success(res.data?.message || 'Deneme başarıyla silindi.');
      onDeleted(id);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Deneme silinemedi.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      data-modal="deneme-duzenle"
      data-nested-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 10200,
        background: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : '16px',
        boxSizing: 'border-box',
        animation: 'fadeIn 0.18s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card-bg,#fff)',
          width: '100%',
          maxWidth: isMobile ? '100%' : 520,
          borderRadius: isMobile ? '22px 22px 0 0' : 18,
          overflowY: 'auto',
          boxSizing: 'border-box',
          boxShadow: '0 24px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(0,0,0,0.08)',
          animation: isMobile ? 'slideUpSheet 0.3s cubic-bezier(.22,1,.36,1)' : 'slideUp 0.22s ease',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 22px', borderBottom: '1px solid var(--border-color,#e5e7eb)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <h3 style={{ margin: 0, fontWeight: 900, fontSize: '1.1rem', color: 'var(--text-primary)' }}>
              ✏️ Denemeyi Düzenle
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              Sınav başlığını ve türünü güncelleyin.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Kapat"
            style={{
              background: 'var(--bg-subtle,#f1f5f9)', border: 'none',
              width: 32, height: 32, borderRadius: '50%', cursor: 'pointer',
              fontSize: '0.95rem', color: 'var(--text-muted)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ✕
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSave} style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" style={{ fontWeight: 700, fontSize: '0.85rem' }}>Deneme Başlığı</label>
            <input
              className="form-input"
              value={baslik}
              onChange={(e) => setBaslik(e.target.value)}
              placeholder="Örn: 8. Sınıf Kurumsal Deneme-1"
              required
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" style={{ fontWeight: 700, fontSize: '0.85rem' }}>Sınav Formatı / Türü</label>
            <select
              className="form-input"
              value={tur}
              onChange={(e) => setTur(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box' }}
            >
              <option value="TYT">TYT (Temel Yeterlilik Testi)</option>
              <option value="AYT">AYT (Alan Yeterlilik Testi)</option>
              <option value="LGS">LGS (Liselere Geçiş Sistemi)</option>
            </select>
          </div>

          {/* Meta Bilgiler */}
          <div style={{
            background: 'var(--bg-subtle,#f8fafc)',
            border: '1px solid var(--border-color,#e5e7eb)',
            borderRadius: 12, padding: '12px 14px',
            fontSize: '0.78rem', color: 'var(--text-muted)',
            display: 'flex', flexDirection: 'column', gap: 6,
          }}>
            <div><b>Dosya:</b> {pick(kayit, ['dosya_adi', 'dosyaAdi', 'fileName'], '-')}</div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <span><b>Toplam:</b> {pick(kayit, ['toplam_ogrenci', 'toplamOgrenci'])}</span>
              <span><b>Kurum:</b> {pick(kayit, ['kurum_ogrencisi_sayisi', 'kurumOgrencisiSayisi'])}</span>
              <span><b>Misafir:</b> {pick(kayit, ['misafir_ogrenci_sayisi', 'misafirOgrenciSayisi'])}</span>
            </div>
            <div><b>Yüklenme Tarihi:</b> {formatDate(pick(kayit, ['created_at', 'createdAt']))}</div>
          </div>

          {/* Sınav PDF'ini Değiştirme / Yenileme */}
          <div style={{
            background: 'var(--bg-subtle,#f8fafc)',
            border: '1.5px dashed var(--accent-border,#cbd5e1)',
            borderRadius: 12, padding: '12px 14px',
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-primary)', margin: 0 }}>
                🔄 Sınav PDF'ini Değiştir (Opsiyonel)
              </label>
              {yeniPdf && (
                <button
                  type="button"
                  onClick={() => {
                    setYeniPdf(null);
                    if (editFileRef.current) editFileRef.current.value = '';
                  }}
                  className="btn btn-outline btn-sm"
                  style={{ color: '#ef4444', borderColor: '#fca5a5', padding: '1px 8px', height: 24, fontSize: '0.72rem' }}
                >
                  ✕ Temizle
                </button>
              )}
            </div>
            <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Öğrenci sonuçlarını farklı bir PDF dosyası ile tamamen yenilemek isterseniz buradan yeni PDF seçebilirsiniz.
            </p>
            <input
              ref={editFileRef}
              type="file"
              accept="application/pdf,.pdf"
              className="form-input"
              style={{ fontSize: '0.8rem', padding: '6px 8px' }}
              onChange={(e) => setYeniPdf(e.target.files?.[0] ?? null)}
            />
            {yeniPdf && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleReplacePdf}
                disabled={replacingPdf || saving || deleting}
                style={{ height: 38, fontSize: '0.82rem', justifyContent: 'center' }}
              >
                {replacingPdf ? 'PDF Güncelleniyor…' : '📤 Yeni PDF ile Sonuçları Güncelle'}
              </button>
            )}
          </div>

          {/* Footer Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={handleDelete}
              disabled={saving || deleting}
              style={{
                color: '#ef4444', borderColor: '#fca5a5',
                fontSize: '0.82rem', height: 38, padding: '0 14px',
              }}
            >
              {deleting ? 'Siliniyor…' : '🗑️ Denemeyi Sil'}
            </button>

            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={onClose}
                disabled={saving || deleting}
                style={{ fontSize: '0.82rem', height: 38, padding: '0 16px' }}
              >
                Vazgeç
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving || deleting}
                style={{ fontSize: '0.82rem', height: 38, padding: '0 18px' }}
              >
                {saving ? 'Kaydediliyor…' : '💾 Kaydet'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Büyük Sonuçlar Modalı ───────────────────────────────────────────────────
// z-index: 10000
// Denemeye tıklandığında açılan büyük ve ferah modal

function DenemeSonuclariModal({
  kayit,
  sonuclar,
  loading,
  filtre,
  onFiltreChange,
  onClose,
  onDuzenle,
  canManage,
}) {
  const w = useWindowWidth();
  const isMobile = w < 768;

  useModalScrollLock(true);

  useEffect(() => {
    const fn = (e) => {
      if (e.key === 'Escape') {
        // Eğer üstte bir nested modal (karne veya düzenleme) açıksa bu modalı kapatma!
        if (document.querySelector('[data-nested-modal="true"]')) {
          return;
        }
        onClose();
      }
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      data-modal="deneme-sonuclari"
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : '18px',
        boxSizing: 'border-box',
        animation: 'fadeIn 0.2s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card-bg, #fff)',
          width: '100%',
          maxWidth: isMobile ? '100%' : 1380,
          height: isMobile ? '96dvh' : '90vh',
          maxHeight: isMobile ? '96dvh' : '92vh',
          borderRadius: isMobile ? '22px 22px 0 0' : 20,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: isMobile
            ? '0 -8px 40px rgba(0,0,0,0.30)'
            : '0 25px 60px rgba(0,0,0,0.32), 0 0 0 1px rgba(0,0,0,0.06)',
          animation: isMobile ? 'slideUpSheet 0.3s cubic-bezier(.22,1,.36,1)' : 'slideUp 0.25s ease',
        }}
      >
        {/* Sticky Header */}
        <div style={{
          padding: isMobile ? '14px 16px' : '18px 24px',
          borderBottom: '1px solid var(--border-color, #e5e7eb)',
          background: 'var(--card-bg, #fff)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexShrink: 0,
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h3 style={{
                margin: 0,
                fontWeight: 900,
                fontSize: isMobile ? '1.05rem' : '1.3rem',
                color: 'var(--text-primary)',
                wordBreak: 'break-word',
              }}>
                {pick(kayit, ['baslik', 'title'], 'Deneme Sonuçları')}
              </h3>
              <Pill
                label={kayit.tur ?? 'TYT'}
                bg={getTurStyle(kayit.tur ?? 'TYT').bg}
                color={getTurStyle(kayit.tur ?? 'TYT').color}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              <span>📅 {formatDate(pick(kayit, ['created_at', 'createdAt', 'tarih']))}</span>
              {kayit.dosya_adi && <span>· 📄 {kayit.dosya_adi}</span>}
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {!isMobile && (
              <div style={{ display: 'flex', gap: 6, marginRight: 6 }}>
                <Pill label={`👥 ${pick(kayit, ['toplam_ogrenci', 'toplamOgrenci'])} Toplam`} bg="var(--bg-subtle,#f1f5f9)" color="var(--text-primary)" />
                <Pill label={`🏫 ${pick(kayit, ['kurum_ogrencisi_sayisi', 'kurumOgrencisiSayisi'])} Kurum`} bg="#d1fae5" color="#065f46" />
                <Pill label={`🧳 ${pick(kayit, ['misafir_ogrenci_sayisi', 'misafirOgrenciSayisi'])} Misafir`} bg="#e0e7ff" color="#3730a3" />
              </div>
            )}
            {canManage && (
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => onDuzenle(kayit)}
                style={{ height: 34, fontSize: '0.82rem', gap: 4 }}
              >
                ✏️ Düzenle
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Kapat"
              style={{
                background: 'var(--bg-subtle, #f1f5f9)',
                border: 'none',
                width: 36,
                height: 36,
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '1rem',
                color: 'var(--text-muted)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Modal Scrollable Body */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          padding: isMobile ? '12px' : '20px 24px',
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '60px 0' }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                border: '3px solid var(--border-color)', borderTop: '3px solid var(--accent, #6366f1)',
                animation: 'spin 0.9s linear infinite', margin: '0 auto',
              }} />
              <p style={{ marginTop: 14, fontSize: '0.9rem', fontWeight: 600 }}>Sonuçlar yükleniyor…</p>
            </div>
          ) : (
            <SonucTablosu
              sonuclar={sonuclar}
              filtre={filtre}
              onFiltreChange={onFiltreChange}
              title={`${pick(kayit, ['baslik', 'title'], 'Deneme')} — Öğrenci Sonuçları`}
              isInsideModal={true}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Küçük yardımcı bileşenler ───────────────────────────────────────────────

function Pill({ label, bg, color }) {
  return (
    <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '3px 9px', borderRadius: 99, background: bg, color }}>
      {label}
    </span>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontWeight: 800, fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
      {children}
    </div>
  );
}

// ─── Öğrenci Kartı (mobile liste görünümü) ───────────────────────────────────

function OgrenciKart({ row, idx, visibleDers, onClick }) {
  const durum = row.durum;
  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--card-bg,#fff)',
        border: '1px solid var(--border-color,#e5e7eb)',
        borderRadius: 14, padding: '13px 15px', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 9,
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{
          width: 30, height: 30, borderRadius: 8,
          background: 'var(--bg-subtle,#f1f5f9)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.72rem', fontWeight: 800, color: 'var(--text-muted)', flexShrink: 0,
        }}>{idx + 1}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: '0.92rem', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {row.ad_soyad}
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 1 }}>
            {row.sinif_adi ?? '-'}{row.ogrenci_no ? ` · #${row.ogrenci_no}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <Pill
            label={DURUM_LABEL[durum] ?? durum}
            bg={durum === 'kurum_ogrencisi' ? '#d1fae5' : '#e0e7ff'}
            color={durum === 'kurum_ogrencisi' ? '#065f46' : '#3730a3'}
          />
          {row.tur && (
            <Pill
              label={row.tur}
              bg={getTurStyle(row.tur).bg}
              color={getTurStyle(row.tur).color}
            />
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
        {visibleDers.map((d) => row.dersler?.[d] ? (
          <span key={d} style={{
            fontSize: '0.7rem', fontWeight: 700, padding: '3px 8px', borderRadius: 7,
            background: `${DERS_RENK[d]}18`, color: DERS_RENK[d],
          }}>
            {d.split(' ')[0]}: {fmt(row.dersler[d].net)}
          </span>
        ) : null)}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 900, color: 'var(--text-primary)' }}>{fmt(row.toplam_net)} net</span>
          {row.puan && <span style={{ fontSize: '0.76rem', fontWeight: 700, color: 'var(--accent,#6366f1)' }}>{fmt(row.puan)}</span>}
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>›</span>
        </div>
      </div>
    </div>
  );
}

// ─── Sonuç Tablosu ────────────────────────────────────────────────────────────

function SonucTablosu({ sonuclar, filtre, onFiltreChange, title, isInsideModal = false }) {
  const [karneRow, setKarneRow] = useState(null);
  const [search, setSearch] = useState('');
  const w = useWindowWidth();
  const isMobile = w < 768;

  const visibleDers = useMemo(
    () => DERS_ORDER.filter((d) => sonuclar.some((r) => r.dersler?.[d])),
    [sonuclar],
  );

  const filtered = useMemo(() => {
    let list = filtre === 'tum' ? sonuclar : sonuclar.filter((r) => r.durum === filtre);
    if (search.trim()) {
      const q = search.trim().toLocaleLowerCase('tr-TR');
      list = list.filter((r) =>
        String(r.ad_soyad ?? '').toLocaleLowerCase('tr-TR').includes(q) ||
        String(r.ogrenci_no ?? '').includes(q),
      );
    }
    return list;
  }, [sonuclar, filtre, search]);

  return (
    <>
      <div className={isInsideModal ? '' : 'card'} style={{ padding: isInsideModal ? 0 : (isMobile ? '14px 14px' : undefined) }}>
        <div style={{ marginBottom: 12 }}>
          <h3 style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: '0.98rem', margin: '0 0 2px' }}>{title}</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.76rem', margin: 0 }}>
            {filtered.length} / {sonuclar.length} öğrenci · Öğrenciye tıklandığında detaylı karnesi açılır.
          </p>
        </div>

        {/* Arama */}
        <input
          className="form-input"
          style={{ width: '100%', height: 42, fontSize: '0.85rem', boxSizing: 'border-box', marginBottom: 10 }}
          placeholder="🔍  İsim veya öğrenci no ara…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* Filtreler */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {['tum', 'kurum_ogrencisi', 'misafir_ogrenci'].map((f) => (
            <button key={f}
              className={`btn btn-sm ${filtre === f ? 'btn-primary' : 'btn-outline'}`}
              style={{ flex: 1, minWidth: 0, fontSize: '0.76rem', padding: '0 6px', height: 34 }}
              onClick={() => onFiltreChange(f)}>
              {f === 'tum' ? 'Tümü' : f === 'kurum_ogrencisi' ? '🏫 Kurum' : '🧳 Misafir'}
            </button>
          ))}
        </div>

        {/* Mobil: kart listesi / Masaüstü: tablo */}
        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '28px 0', fontSize: '0.85rem' }}>Sonuç bulunamadı.</div>
            ) : filtered.map((row, idx) => (
              <OgrenciKart key={row.id ?? idx} row={row} idx={idx} visibleDers={visibleDers} onClick={() => setKarneRow(row)} />
            ))}
          </div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="table" style={{ minWidth: 700 }}>
              <thead>
                <tr>
                  {['#', 'Ad Soyad', 'No', 'Sınıf', 'Durum', 'Tür'].map((h) => (
                    <th key={h} style={{ whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                  {visibleDers.map((d) => (
                    <th key={d} style={{ color: DERS_RENK[d], whiteSpace: 'nowrap' }}>{d.split(' ')[0]}</th>
                  ))}
                  {['Top. Net', 'Puan', 'Okul', 'Genel'].map((h) => (
                    <th key={h} style={{ whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={14} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '24px 0' }}>Sonuç bulunamadı.</td></tr>
                ) : filtered.map((row, idx) => {
                  const durum = row.durum;
                  return (
                    <tr
                      key={row.id ?? idx}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setKarneRow(row)}
                      title="Öğrenci karnesini aç"
                    >
                      <td style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{idx + 1}</td>
                      <td style={{ fontWeight: 800, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{row.ad_soyad}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{row.ogrenci_no ?? '-'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{row.sinif_adi ?? '-'}</td>
                      <td>
                        <span className={`badge ${durum === 'kurum_ogrencisi' ? 'badge-success' : 'badge-info'}`}>
                          {DURUM_LABEL[durum] ?? durum}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: getTurStyle(row.tur).color }}>
                          {row.tur ?? '-'}
                        </span>
                      </td>
                      {visibleDers.map((d) => (
                        <td key={d} style={{ fontWeight: 600, color: DERS_RENK[d] }}>
                          {row.dersler?.[d] ? fmt(row.dersler[d].net) : '-'}
                        </td>
                      ))}
                      <td style={{ fontWeight: 800 }}>{fmt(row.toplam_net)}</td>
                      <td style={{ fontWeight: 700, color: 'var(--accent)' }}>{fmt(row.puan)}</td>
                      <td>{row.okul_sirasi ?? '-'}</td>
                      <td>{row.genel_siralama ? `#${Number(row.genel_siralama).toLocaleString('tr-TR')}` : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Öğrenci tıklandığında açılan karne modalı (DenemeSonuclariModal'ın üstünde açılır) */}
      {karneRow && <KarneModal row={karneRow} onClose={() => setKarneRow(null)} />}
    </>
  );
}

// ─── Geçmiş Kayıt Kartı (mobile) ─────────────────────────────────────────────

function GecmisKart({ kayit, active, canManage, onClick, onDuzenle }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: active ? 'var(--accent-bg,#f0f4ff)' : 'var(--card-bg,#fff)',
        border: `1.5px solid ${active ? 'var(--accent,#6366f1)' : 'var(--border-color,#e5e7eb)'}`,
        borderRadius: 14, padding: '13px 15px', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 9,
        transition: 'border-color 0.15s, background 0.15s',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ fontWeight: 800, fontSize: '0.92rem', color: 'var(--text-primary)', flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
          {pick(kayit, ['baslik', 'title'], 'Başlıksız')}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <Pill
            label={kayit.tur ?? 'TYT'}
            bg={getTurStyle(kayit.tur ?? 'TYT').bg}
            color={getTurStyle(kayit.tur ?? 'TYT').color}
          />
          {canManage && (
            <button
              type="button"
              className="btn btn-outline btn-sm"
              style={{ fontSize: '0.72rem', padding: '2px 8px', height: 26 }}
              onClick={(e) => {
                e.stopPropagation();
                onDuzenle(kayit);
              }}
              title="Düzenle"
            >
              ✏️ Düzenle
            </button>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <Pill
          label={`🏫 ${pick(kayit, ['kurum_ogrencisi_sayisi', 'kurumOgrencisiSayisi', 'kurum_ogrenci_sayisi'])} kurum`}
          bg="#d1fae5" color="#065f46"
        />
        <Pill
          label={`🧳 ${pick(kayit, ['misafir_ogrenci_sayisi', 'misafirOgrenciSayisi'])} misafir`}
          bg="#e0e7ff" color="#3730a3"
        />
        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
          {formatDate(pick(kayit, ['created_at', 'createdAt', 'tarih'], null))}
        </span>
      </div>
    </div>
  );
}

// ─── Ana Sayfa ────────────────────────────────────────────────────────────────

export default function TurkiyeGeneliDenemeSonuclariPage() {
  const { user } = useAuth();
  const w = useWindowWidth();
  const isMobile = w < 768;
  const canView = ['kurucu', 'rehber_ogretmen', 'muhasebe'].includes(user?.rol);
  const canManage = ['kurucu', 'rehber_ogretmen', 'muhasebe'].includes(user?.rol);

  const [baslik, setBaslik] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [sonuclar, setSonuclar] = useState([]);
  const [gecmis, setGecmis] = useState([]);
  const [seciliKayit, setSeciliKayit] = useState(null);
  const [duzenleKayit, setDuzenleKayit] = useState(null);
  const [detaySonuc, setDetaySonuc] = useState([]);
  const [filtre, setFiltre] = useState('tum');
  const [detayFiltre, setDetayFiltre] = useState('tum');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [sendingResultNotification, setSendingResultNotification] = useState(false);
  const [confirmedUpload, setConfirmedUpload] = useState(null);
  const [detailLoad, setDetailLoad] = useState(false);
  const [pendingPreview, setPendingPreview] = useState(null);
  const fileRef = useRef();

  const handleCancelPreview = async () => {
    const previewId = pendingPreview?.preview_id ?? pendingPreview?.previewId;
    setCancelling(true);
    try {
      if (previewId) {
        await axios.delete(`/turkiye-geneli-deneme-sonuclari/preview/${previewId}`);
      }
      toast.success('Yüklenen PDF kaldırıldı. Yeni dosya yükleyebilirsiniz.');
    } catch (err) {
      toast.info('Önizleme temizlendi.');
    } finally {
      setCancelling(false);
      setPendingPreview(null);
      setSonuclar([]);
      setPdfFile(null);
      setBaslik('');
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const loadGecmis = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/turkiye-geneli-deneme-sonuclari');
      setGecmis(getList(data));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Geçmiş yüklemeler alınamadı.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (canView) loadGecmis();
  }, [canView, loadGecmis]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!pdfFile) { toast.error('Lütfen bir PDF dosyası seçin.'); return; }
    const formData = new FormData();
    formData.append('pdfFile', pdfFile);
    if (baslik.trim()) formData.append('baslik', baslik.trim());
    setUploading(true);
    try {
      const { data } = await axios.post('/turkiye-geneli-deneme-sonuclari/pdf-yukle', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const gelenSonuclar = getDetailResults(data);
      setSonuclar(gelenSonuclar);
      setSeciliKayit(null);
      setDetaySonuc([]);
      setFiltre('tum');
      const preview = data?.data ?? data;
      setPendingPreview(data?.requires_confirmation ? preview : null);
      toast.success(data?.message ?? 'PDF başarıyla yüklendi.');
      setPdfFile(null);
      setBaslik('');
      if (fileRef.current) fileRef.current.value = '';
      if (!data?.requires_confirmation) await loadGecmis();
    } catch (err) {
      toast.error(err.response?.data?.message || 'PDF yüklenemedi.');
    } finally { setUploading(false); }
  };

  const handleConfirmUpload = async () => {
    const previewId = pendingPreview?.preview_id ?? pendingPreview?.previewId;
    if (!previewId) return;
    setConfirming(true);
    try {
      const { data } = await axios.post('/turkiye-geneli-deneme-sonuclari/onayla', {
        preview_id: previewId,
        baslik: pendingPreview?.baslik,
      });
      setSonuclar(getDetailResults(data));
      const savedUpload = data?.data ?? data;
      setConfirmedUpload(savedUpload?.id ? { id: savedUpload.id, baslik: savedUpload.baslik } : null);
      setPendingPreview(null);
      toast.success(data?.message ?? 'Deneme sonuçları kaydedildi.');
      await loadGecmis();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Deneme sonuçları kaydedilemedi.');
    } finally {
      setConfirming(false);
    }
  };

  const handleSendResultNotification = async () => {
    if (!confirmedUpload?.id || sendingResultNotification) return;
    setSendingResultNotification(true);
    try {
      const { data } = await axios.post(
        `/turkiye-geneli-deneme-sonuclari/${confirmedUpload.id}/bildirim-gonder`
      );
      const summary = data?.data ?? {};
      toast.success(data?.message || `${summary.queued ?? 0} bildirim kuyruğa eklendi.`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Deneme sonucu bildirimleri kuyruğa eklenemedi.');
    } finally {
      setSendingResultNotification(false);
    }
  };

  const handleGecmisDetay = async (kayit) => {
    const id = kayit?.id ?? kayit?._id;
    if (!id) return;
    setSeciliKayit(kayit);
    setDetailLoad(true);
    setDetayFiltre('tum');
    try {
      const { data } = await axios.get(`/turkiye-geneli-deneme-sonuclari/${id}`);
      setDetaySonuc(getDetailResults(data));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Detay sonuçları alınamadı.');
      setDetaySonuc([]);
    } finally { setDetailLoad(false); }
  };

  const handleDenemeSaved = (updated) => {
    const updatedId = updated?.id ?? updated?._id;
    setGecmis((prev) =>
      prev.map((k) => ((k.id ?? k._id) === updatedId ? { ...k, ...updated } : k))
    );
    if (seciliKayit && (seciliKayit.id ?? seciliKayit._id) === updatedId) {
      const isTurChanged = updated.tur && updated.tur !== seciliKayit.tur;
      setSeciliKayit((prev) => ({ ...prev, ...updated }));
      if (isTurChanged) {
        handleGecmisDetay({ ...seciliKayit, ...updated });
      }
    }
  };

  const handleDenemeDeleted = (deletedId) => {
    setGecmis((prev) => prev.filter((k) => (k.id ?? k._id) !== deletedId));
    if (seciliKayit && (seciliKayit.id ?? seciliKayit._id) === deletedId) {
      setSeciliKayit(null);
      setDetaySonuc([]);
    }
  };

  if (!canView) {
    return (
      <div className="card">
        <h2 style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, margin: 0 }}>Yetkisiz Erişim</h2>
        <p style={{ color: 'var(--text-muted)', marginTop: 8 }}>Bu sayfayı görüntüleme yetkiniz bulunmuyor.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 12 : 20 }}>
      {/* Dynamic Keyframes for smooth animations */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideUpSheet {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
      `}</style>

      {/* Başlık */}
      <div>
        <h2 style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: 'clamp(1.05rem,4vw,1.4rem)', margin: 0 }}>
          Türkiye Geneli Deneme Sonuçları
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', margin: '4px 0 0' }}>
          {canManage
            ? 'TYT, AYT veya LGS PDF dosyasını yükleyin. Sınav formatı ve kurum öğrencileri otomatik algılanır.'
            : 'Kurum öğrencilerinin Türkiye geneli deneme sonuçlarını ve geçmiş yüklemelerini inceleyin.'}
        </p>
      </div>

      {/* Upload formu */}
      {canManage && !uploading && !pendingPreview && (
        <div className="card" style={{ borderColor: 'var(--accent-border)', padding: isMobile ? '14px' : undefined }}>
          <form onSubmit={handleUpload} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Başlık (opsiyonel)</label>
              <input className="form-input" value={baslik} onChange={(e) => setBaslik(e.target.value)}
                placeholder="Örn: 8. Sınıf Kurumsal Deneme-1"
                style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <label className="form-label" style={{ margin: 0 }}>PDF Dosyası</label>
                {pdfFile && (
                  <button
                    type="button"
                    onClick={() => {
                      setPdfFile(null);
                      if (fileRef.current) fileRef.current.value = '';
                    }}
                    className="btn btn-outline btn-sm"
                    style={{ color: '#ef4444', borderColor: '#fca5a5', padding: '1px 8px', height: 24, fontSize: '0.74rem' }}
                  >
                    ✕ Seçimi Temizle
                  </button>
                )}
              </div>
              <input ref={fileRef} className="form-input" type="file" accept="application/pdf,.pdf"
                onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
                style={{ width: '100%', boxSizing: 'border-box' }} />
              {pdfFile && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '7px 12px', background: 'var(--bg-subtle,#f1f5f9)',
                  borderRadius: 8, fontSize: '0.8rem', marginTop: 6,
                }}>
                  <span style={{ fontWeight: 600, wordBreak: 'break-all' }}>
                    📄 {pdfFile.name} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({(pdfFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                  </span>
                </div>
              )}
            </div>
            <button className="btn btn-primary" type="submit"
              disabled={!pdfFile}
              style={{ width: '100%', justifyContent: 'center', height: 46, fontSize: '0.92rem' }}>
              📤 PDF Yükle &amp; Analiz Et
            </button>
          </form>
        </div>
      )}

      {canManage && uploading && <UploadAnimation fileName={pdfFile?.name ?? 'PDF'} />}

      {canManage && !uploading && pendingPreview && (
        <div className="card" style={{ borderColor: '#10b981', padding: isMobile ? 14 : 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, fontSize: '1.02rem', fontWeight: 900 }}>✅ PDF Analiz Edildi</h3>
                <span className="badge badge-success" style={{ fontSize: '0.75rem' }}>
                  {pendingPreview.tur}
                </span>
                {pendingPreview.dosya_adi && (
                  <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>
                    ({pendingPreview.dosya_adi})
                  </span>
                )}
              </div>
              <p style={{ margin: '6px 0 0', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                <b>{pendingPreview.toplam_ogrenci}</b> öğrenci okundu · <b>{pendingPreview.kurum_ogrencisi_sayisi}</b> kurum öğrencisi · <b>{pendingPreview.misafir_ogrenci_sayisi}</b> misafir
              </p>
              {(pendingPreview.analysis?.warnings ?? []).map((warning) => (
                <div key={warning} style={{ marginTop: 5, color: '#b45309', fontSize: '0.76rem' }}>⚠️ {warning}</div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
              <button
                className="btn btn-outline"
                type="button"
                onClick={handleCancelPreview}
                disabled={cancelling || confirming}
                style={{
                  color: '#ef4444',
                  borderColor: '#fca5a5',
                  justifyContent: 'center',
                  minWidth: isMobile ? '100%' : 150,
                  height: 42,
                  fontSize: '0.85rem',
                  fontWeight: 600,
                }}
                title="Yüklenen bu PDF'i kaldırıp yerine yenisini ekleyin"
              >
                {cancelling ? 'Kaldırılıyor…' : '🗑️ PDF\'i Kaldır & Yenisini Seç'}
              </button>

              <button
                className="btn btn-primary"
                type="button"
                onClick={handleConfirmUpload}
                disabled={confirming || cancelling}
                style={{
                  minWidth: isMobile ? '100%' : 190,
                  justifyContent: 'center',
                  height: 42,
                  fontSize: '0.88rem',
                }}
              >
                {confirming ? 'Kaydediliyor…' : '💾 Sonuçları Onayla ve Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Yükleme / Önizleme tablosu */}
      {canManage && !uploading && sonuclar.length > 0 && (
        <SonucTablosu
          sonuclar={sonuclar}
          filtre={filtre}
          onFiltreChange={setFiltre}
          title={pendingPreview ? '🔍 Kayıt Öncesi Önizleme' : '✅ Yükleme Sonuçları'}
        />
      )}

      {canManage && confirmedUpload?.id && (
        <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', borderColor: 'var(--accent-border)', padding: isMobile ? 14 : 18 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '0.98rem', fontWeight: 900 }}>📲 Veli bilgilendirmesi</h3>
            <p style={{ margin: '5px 0 0', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
              {confirmedUpload.baslik || 'Bu deneme'} için kurum öğrencilerinin velilerine sonuç mesajı gönderin.
            </p>
          </div>
          <button
            className="btn btn-primary"
            type="button"
            onClick={handleSendResultNotification}
            disabled={sendingResultNotification}
            style={{ justifyContent: 'center', minHeight: 42, width: isMobile ? '100%' : 'auto' }}
          >
            {sendingResultNotification ? 'Kuyruğa ekleniyor…' : 'Deneme sınav sonucu bilgilendirme mesajı gönder'}
          </button>
        </div>
      )}

      {/* Geçmiş yüklemeler */}
      <div className="card" style={{ padding: isMobile ? '14px' : undefined }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div>
            <h3 style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: '0.98rem', margin: 0 }}>Geçmiş Yüklemeler</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.76rem', margin: '2px 0 0' }}>Tıklayarak sonuçları büyük modalda görüntüleyin veya düzenleyin.</p>
          </div>
          <button className="btn btn-outline btn-sm" onClick={loadGecmis} disabled={loading} style={{ flexShrink: 0 }}>
            {loading ? '…' : '🔄 Yenile'}
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '24px 0', fontSize: '0.85rem' }}>Yükleniyor…</div>
        ) : gecmis.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '24px 0', fontSize: '0.85rem' }}>Henüz yükleme yapılmamış.</div>
        ) : isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {gecmis.map((kayit, i) => {
              const id = kayit.id ?? kayit._id ?? i;
              const active = seciliKayit && (seciliKayit.id ?? seciliKayit._id) === (kayit.id ?? kayit._id);
              return (
                <GecmisKart
                  key={id}
                  kayit={kayit}
                  active={active}
                  canManage={canManage}
                  onClick={() => handleGecmisDetay(kayit)}
                  onDuzenle={() => setDuzenleKayit(kayit)}
                />
              );
            })}
          </div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="table" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  {['Başlık', 'Tür', 'Dosya', 'Toplam', 'Kurum', 'Misafir', 'Tarih', 'İşlemler'].map((h) => (
                    <th key={h} style={{ whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gecmis.map((kayit, i) => {
                  const id = kayit.id ?? kayit._id ?? i;
                  const active = seciliKayit && (seciliKayit.id ?? seciliKayit._id) === (kayit.id ?? kayit._id);
                  return (
                    <tr
                      key={id}
                      onClick={() => handleGecmisDetay(kayit)}
                      style={{ cursor: 'pointer', background: active ? 'var(--accent-bg,#f0f4ff)' : undefined, transition: 'background 0.2s' }}
                      title="Sonuçları modalda görüntüle"
                    >
                      <td style={{ fontWeight: 800, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                        {pick(kayit, ['baslik', 'title'], 'Başlıksız')}
                      </td>
                      <td>
                        <span style={{ fontWeight: 700, fontSize: '0.78rem', color: getTurStyle(kayit.tur ?? 'TYT').color }}>
                          {kayit.tur ?? 'TYT'}
                        </span>
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.82rem', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {pick(kayit, ['dosya_adi', 'dosyaAdi', 'fileName', 'filename'])}
                      </td>
                      <td>{pick(kayit, ['toplam_ogrenci', 'toplamOgrenci', 'toplam'])}</td>
                      <td><span className="badge badge-success">{pick(kayit, ['kurum_ogrencisi_sayisi', 'kurumOgrencisiSayisi', 'kurum_ogrenci_sayisi'])}</span></td>
                      <td><span className="badge badge-info">{pick(kayit, ['misafir_ogrenci_sayisi', 'misafirOgrenciSayisi'])}</span></td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                        {formatDate(pick(kayit, ['created_at', 'createdAt', 'tarih'], null))}
                      </td>
                      <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            style={{ fontSize: '0.76rem', padding: '3px 8px', height: 28 }}
                            onClick={() => handleGecmisDetay(kayit)}
                            title="Sonuçları Görüntüle"
                          >
                            👁️ İncele
                          </button>
                          {canManage && (
                            <button
                              type="button"
                              className="btn btn-outline btn-sm"
                              style={{ fontSize: '0.76rem', padding: '3px 8px', height: 28 }}
                              onClick={() => setDuzenleKayit(kayit)}
                              title="Denemeyi Düzenle"
                            >
                              ✏️ Düzenle
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Büyük Sonuçlar Modalı (Denemeye tıklandığında açılır) */}
      {seciliKayit && (
        <DenemeSonuclariModal
          kayit={seciliKayit}
          sonuclar={detaySonuc}
          loading={detailLoad}
          filtre={detayFiltre}
          onFiltreChange={setDetayFiltre}
          onClose={() => {
            setSeciliKayit(null);
            setDetaySonuc([]);
          }}
          onDuzenle={(k) => setDuzenleKayit(k)}
          canManage={canManage}
        />
      )}

      {/* Deneme Düzenle Modalı (En üstte açılır) */}
      {duzenleKayit && (
        <DenemeDuzenleModal
          kayit={duzenleKayit}
          onClose={() => setDuzenleKayit(null)}
          onSaved={handleDenemeSaved}
          onDeleted={handleDenemeDeleted}
        />
      )}
    </div>
  );
}
